import React, { useState, useEffect } from 'react';
import { Typography, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip } from '@mui/material';
import { IoMdCloseCircleOutline } from "react-icons/io";
import { FiExternalLink, FiCopy } from "react-icons/fi";
import { NotificationManager } from 'react-notifications';
import ApiRequestService from '../../component/ApiRequestService';
import LoadingSpinner from '../../component/LoadingSpinner';
import { AppProperties, ADMIN_USER_ROLE } from '../../common/function';

/**
 * The server's answer for a library row that belongs to another university
 * which has since switched sync off. Listings drop the row on their own, so
 * this only ever reaches a page that was opened from a stale link or bookmark.
 * Kept in step with assignment_matrix_library.ErrAMLNotVisible on the server.
 */
export const AML_NOT_SHARED_MESSAGE = "this library entry is no longer shared";

export const isAMLNotShared = (res) => (
    res && res.data && res.data.status !== 0 && res.data.message === AML_NOT_SHARED_MESSAGE
);

/**
 * Why every write control disappears on a received row. The source university
 * owns it: a rename here would be overwritten by the next push, and a delete
 * would never reach the source at all.
 */
export const READ_ONLY_HINT = "This belongs to another university. Only the university that owns it can make changes.";

// sync_status_code, as the server writes it and the toggle colours by it.
export const SYNC_STATUS = {
    OFF: 0,
    IN_PROGRESS: 1,
    SUCCESS: 2,
    FAILURE: 3,
};

// How often to re-read status while a push is running. Long enough not to
// hammer the endpoint, short enough that a failure surfaces while the person
// who caused it is still looking at the page.
const STATUS_POLL_MS = 4000;

/**
 * How long a sync toast stays up. These messages name a university, a subject
 * or a reason a write was refused, and four seconds was not enough to read one
 * before it disappeared - the user then has no way of getting it back.
 */
export const SYNC_NOTIFICATION_MS = 12000;

/**
 * True when every id is a positive integer. The library ids come off the URL,
 * so a missing param leaves them null or NaN, which the server reads as 0 - and
 * course assignments carry aml ids of 0. Deletes check this before sending.
 */
export const hasValidLibraryIds = (...ids) => ids.every(id => {
    const n = Number(id);
    return Number.isInteger(n) && n > 0;
});

/**
 * What each library level must hold before its switch may go on. A push of an
 * empty subject, topic or matrix carries nothing across and leaves the other
 * university with a bare row it cannot use or tidy up - only the source can
 * change it - so the switch is refused here instead. Counts come off the
 * listing rows, which is what the pages already draw.
 */
const EMPTY_ROW_CHECKS = {
    subject: [row => row.total_topics_count, "This subject has no topics. Add at least one topic before syncing it."],
    topic: [row => row.total_matrix_count, "This topic has no matrices. Add at least one matrix before syncing it."],
    matrix: [row => row.total_quiz_count, "This matrix has no assignments. Add at least one assignment before syncing it."],
};

/**
 * Refuses to switch sync on for a row with nothing under it. Returns true when
 * the switch was refused (and the message shown), so the caller just returns;
 * false means the row can go to the server.
 */
export const refuseEmptyRowSync = (level, row) => {
    const [count, message] = EMPTY_ROW_CHECKS[level];
    if ((Number(count(row)) || 0) > 0) return false;

    NotificationManager.error(message, "ERROR", SYNC_NOTIFICATION_MS);
    return true;
}

/**
 * The Sync cell for a row this university received. There is no switch to draw
 * - the source owns it - so the cell says so and offers the only useful action
 * left: open the same page at the university that can actually change it.
 *
 * The ids are allocated in blocks and pushed verbatim, so the row has the same
 * ids at the source and `sourcePath` addresses it there unchanged. The base URL
 * is not guessable from here, which is what the lookup is for.
 *
 * Tutorials resolve their source through their own endpoint, so they pass
 * `lookupUrl` and `lookupData`; library rows leave both to the defaults.
 */
export const SyncedAtSourceCell = ({ amlSubjectID, sourcePath, lookupUrl = '/amlSourceUnivUrl', lookupData }) => {

    const [loading, setLoading] = useState(false);

    const openAtSource = () => {
        setLoading(true);
        ApiRequestService({
            method: 'post',
            url: lookupUrl,
            data: lookupData || { aml_subject_id: amlSubjectID },
        }).then(res => {
            const settings = res && res.data && res.data.data;
            const sourceBase = settings && settings.REACT_APP_URL;

            if (!sourceBase) {
                NotificationManager.error(
                    (res && res.data && res.data.message) || "Could not find the university that owns this",
                    "ERROR", SYNC_NOTIFICATION_MS);
                return;
            }

            window.open(sourceBase + sourcePath, '_blank');
        }).catch(() => {
            NotificationManager.error("Could not find the university that owns this", "ERROR", SYNC_NOTIFICATION_MS);
        }).finally(() => {
            setLoading(false);
        });
    }

    return (
        <Tooltip arrow placement="top" title={
            <Typography className="text-white" fontSize={14}>
                Owned by another university. Open it there to make changes.
            </Typography>
        }>
            <Button
                size="small"
                variant="text"
                disabled={loading}
                onClick={openAtSource}
                endIcon={<FiExternalLink />}
                sx={{ color: "#727C8E", textTransform: "none", marginTop: "4px" }}
            >
                Synced
            </Button>
        </Tooltip>
    );
}

/**
 * Stands in for the Sync toggle until the first status read lands.
 *
 * The toggle draws itself from that status, so before it arrives every switch
 * drew as off and then flipped on a moment later - which reads as sync having
 * been off, or as the page having turned it on by itself. The question bank
 * swaps in a spinner for exactly this (component/TableRow.js), so the library
 * does the same.
 *
 * cls is emptied deliberately: the default loadingSpinnerContainer carries 30%
 * vertical margins, which is meant for a whole widget and would double the
 * height of a table row.
 */
export const SyncLoadingCell = () => (
    <div style={{ marginTop: "8px" }}>
        <LoadingSpinner loading={true} cls="" />
    </div>
);

/**
 * Stands in for the Actions column on a received row. A blank cell reads as a
 * rendering fault, so the row says what it is instead.
 */
export const ReadOnlyActionsCell = () => (
    <Tooltip arrow placement="top" title={
        <Typography className="text-white" fontSize={14}>{READ_ONLY_HINT}</Typography>
    }>
        <span style={{ color: "#727C8E", fontSize: "0.8rem", cursor: "default" }}>Read only</span>
    </Tooltip>
);

/**
 * Shared sync wiring for the three library pages. Each level (subject, topic,
 * matrix) reads a status list and posts to a toggle endpoint; only the URLs and
 * the payload shape differ.
 *
 * The server answers a refused enable with status 0 and a non-empty
 * unsynced_categories list rather than an error, so the caller can name exactly
 * what is blocking instead of showing a bare failure.
 */
export const useLibrarySync = ({ statusUrl, statusMethod = 'post', toggleUrl }) => {

    const [syncData, setSyncData] = useState([]);
    const [syncLoading, setSyncLoading] = useState(false);
    // True until the first status read settles, so the pages can show a spinner
    // instead of a switch whose state is not known yet. Later reads and the
    // in-progress poll leave it alone: the toggle is already drawn by then, and
    // blinking it away every four seconds is worse than letting it sit.
    const [syncStatusLoading, setSyncStatusLoading] = useState(true);
    const [unsyncedCategories, setUnsyncedCategories] = useState(null);
    const [lastStatusPayload, setLastStatusPayload] = useState(null);
    const [syncAccess, setSyncAccess] = useState(false);
    const [statusRequested, setStatusRequested] = useState(false);

    /**
     * Who may switch cross-university sync on for the library. Its own endpoint
     * rather than the question bank's /syncSwitch, so the two can be opened to
     * different people and the question bank's gate is left as it is.
     *
     * Non-admins are not asked at all, matching pages/subjects.js, and the
     * answer defaults to no: an endpoint that cannot be reached should not hand
     * out the switch.
     */
    useEffect(() => {
        if (AppProperties().currentUserData?.role !== ADMIN_USER_ROLE) return;

        ApiRequestService({
            method: 'post',
            url: '/amlSyncSwitch',
        }).then(res => {
            setSyncAccess(!!(res && res.data && res.data.data));
        }).catch(() => {
            setSyncAccess(false);
        });
    }, []);

    const fetchSyncStatus = (payload) => {
        ApiRequestService({
            method: statusMethod,
            url: statusUrl,
            data: payload,
        }).then(res => {
            if (res && res.data && res.data.data) {
                setSyncData(res.data.data);
            } else {
                setSyncData([]);
            }
        }).catch(() => {
            setSyncData([]);
        }).finally(() => {
            setSyncStatusLoading(false);
        });
    }

    // Records what to ask for; the effect below decides when. Status exists
    // only to draw the toggle, and the toggle needs access, so there is nothing
    // to fetch for someone who will not see it.
    const loadSyncStatus = (payload) => {
        setLastStatusPayload(payload || null);
        setStatusRequested(true);
    }

    // Access resolves after the page has already asked, so the first read waits
    // here rather than firing a request the sync routes would refuse.
    useEffect(() => {
        if (!syncAccess || !statusRequested) return;
        fetchSyncStatus(lastStatusPayload);
    }, [syncAccess, statusRequested, lastStatusPayload]);

    /**
     * Re-reads status with whatever payload the page last used. Callers reach
     * for this after a mutation: every library write queues a push, and a row
     * created under an already-enabled parent inherits the switch server-side,
     * so the toggles are stale the moment an edit lands.
     */
    const reloadSyncStatus = () => {
        if (!syncAccess) return;
        fetchSyncStatus(lastStatusPayload);
    }

    const getSyncInfo = (id) => {
        return syncData.find(item => item.id === id) || {};
    }

    // A push runs on a server-side queue, so a row sitting at "in progress"
    // only moves to green or red when something asks again. Nothing did, which
    // left a failed push invisible until a manual page reload - and recovery is
    // a manual off-and-on, so it only works once the failure can be seen.
    // The poll stops as soon as no row is mid-push.
    useEffect(() => {
        const pushing = syncData.some(item => item.sync_status_code === SYNC_STATUS.IN_PROGRESS);
        if (!pushing) return undefined;

        const timer = setInterval(() => reloadSyncStatus(), STATUS_POLL_MS);
        return () => clearInterval(timer);
    }, [syncData]);

    // Optimistic: flip the switch locally, then reconcile against the server.
    const setSyncLocally = (id, enableSync) => {
        setSyncData(current => current.map(item => (
            item.id === id ? { ...item, enable_sync: enableSync, some_sync: false } : item
        )));
    }

    const toggleSync = (id, enableSync, payload) => {
        setSyncLoading(true);
        setSyncLocally(id, enableSync);

        ApiRequestService({
            method: 'post',
            url: toggleUrl,
            data: { ...payload, enable_sync: enableSync },
        }).then(res => {
            setSyncLoading(false);

            if (!res || !res.data || res.data.status !== 0) {
                NotificationManager.error((res && res.data && res.data.message) || "Could not update sync", "ERROR", SYNC_NOTIFICATION_MS);
                reloadSyncStatus();
                return;
            }

            const result = res.data.data;
            if (result && result.valid === false) {
                // Refused: the question bank has not caught up yet.
                setUnsyncedCategories(result.unsynced_categories || []);
                reloadSyncStatus();
                return;
            }

            reloadSyncStatus();
        }).catch(() => {
            setSyncLoading(false);
            NotificationManager.error("Could not update sync", "ERROR", SYNC_NOTIFICATION_MS);
            reloadSyncStatus();
        });
    }

    return {
        syncData,
        syncAccess,
        syncLoading,
        syncStatusLoading,
        loadSyncStatus,
        reloadSyncStatus,
        getSyncInfo,
        toggleSync,
        unsyncedCategories,
        clearUnsyncedCategories: () => setUnsyncedCategories(null),
    };
}

// Column order for the dialog table and for the text the copy button puts on
// the clipboard - one list so the two cannot drift apart.
//
// No university column: the server reports each category once, not once per
// university that is short of it, because syncing the category from the
// question bank is the same job whichever of them are missing it.
const UNSYNCED_COLUMNS = [
    ["Matrix", row => row.matrix_name],
    ["Subject", row => row.subject_name || row.subject_id],
    ["Topic", row => row.topic_name || row.topic_id],
    ["Category", row => row.category_name || row.category_id],
    ["Subcategory", row => (row.subcategory_id ? (row.subcategory_name || row.subcategory_id) : "-")],
    ["Missing", row => row.missing_level],
];

// Column order for the dialog listing the deleted question bank rows that old
// assignments still use, which refuses an import into a library matrix. The
// assignment comes first: it is the thing that has to be fixed. A hard deleted
// subcategory has no name left anywhere, so it falls back to its id.
const DELETED_COLUMNS = [
    ["Assignment", row => row.quiz_name || row.quiz_id],
    ["Subject", row => row.subject_name || row.subject_id],
    ["Topic", row => row.topic_name || row.topic_id],
    ["Category", row => row.category_name || row.category_id],
    ["Subcategory", row => (row.subcategory_id ? (row.subcategory_name || row.subcategory_id) : "-")],
    ["Deleted", row => row.deleted_level],
];

// How a library matrix depends on a question bank category, as the server
// names it in used_by. Kept in step with sync.AMLUsedBy* on the server.
const USED_BY_LABELS = {
    matrix: "On the matrix",
    assignment: "Used by an assignment",
};

// Column order for the dialog listing the library matrices that are holding a
// question bank category's sync switch on.
const LIBRARY_USAGE_COLUMNS = [
    ["Library subject", row => row.aml_subject_name || row.aml_subject_id],
    ["Library topic", row => row.aml_topic_name || row.aml_topic_id],
    ["Matrix", row => row.matrix_name || row.aml_matrix_id],
    ["Depends on it", row => USED_BY_LABELS[row.used_by] || row.used_by],
];

// Tab separated, so it pastes into a spreadsheet, a ticket or a chat message
// as a table rather than one run-on line.
const rowsAsText = (columns, rows) => (
    [columns.map(([label]) => label).join("\t")]
        .concat(rows.map(row => columns.map(([, read]) => `${read(row) ?? ""}`).join("\t")))
        .join("\n")
);

/**
 * A refusal, listed. Both sync gates refuse the same way - a table of the rows
 * that are blocking, plus the reason - so they draw the same dialog and differ
 * only in their columns and their wording.
 *
 * The list is what the person has to hand to whoever owns the other end, so it
 * can be copied rather than retyped off the screen.
 */
const BlockingRowsDialog = ({ rows, columns, onClose, title, description }) => {

    const open = Array.isArray(rows);
    const safeRows = rows || [];

    const copyRows = () => {
        // Absent outside a secure context, where it throws on the call rather
        // than rejecting, so the button would do nothing at all.
        if (!navigator.clipboard) {
            NotificationManager.error("Could not copy the list", "ERROR", SYNC_NOTIFICATION_MS);
            return;
        }

        navigator.clipboard.writeText(rowsAsText(columns, safeRows)).then(() => {
            NotificationManager.success("The list was copied to your clipboard.", "");
        }).catch(() => {
            NotificationManager.error("Could not copy the list", "ERROR", SYNC_NOTIFICATION_MS);
        });
    }

    return (
        <Dialog open={open} maxWidth="md" fullWidth onClose={onClose}>
            <DialogTitle>
                <Typography fontSize={18} fontWeight={500}>
                    {title}
                </Typography>
            </DialogTitle>
            <IconButton
                aria-label="close"
                sx={{ position: 'absolute', right: 8, top: 8 }}
                onClick={onClose}
            >
                <IoMdCloseCircleOutline fontSize={24} />
            </IconButton>
            <DialogContent>
                <Typography fontSize={14} style={{ marginBottom: '1rem' }}>
                    {description}
                </Typography>

                <TableContainer component={Paper} style={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {columns.map(([label]) => (
                                    <TableCell key={label}><strong>{label}</strong></TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {safeRows.length === 0 ?
                                <TableRow>
                                    <TableCell colSpan={columns.length}>Nothing to show.</TableCell>
                                </TableRow>
                                : safeRows.map((row, index) => (
                                    <TableRow key={index}>
                                        {columns.map(([label, read]) => (
                                            <TableCell key={label}>{read(row)}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button
                    variant='outlined'
                    startIcon={<FiCopy />}
                    disabled={safeRows.length === 0}
                    onClick={copyRows}
                >
                    Copy list
                </Button>
                <Button variant='contained' onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * Lists what the target universities are missing.
 *
 * Two callers with the same list and different reasons: the sync toggle, which
 * refuses to switch on, and the matrix page, which refuses to add a category to
 * a matrix that is already syncing. `title` and `description` carry the reason;
 * the table below them is identical either way.
 */
export const UnsyncedCategoriesDialog = ({
    unsyncedCategories,
    onClose,
    title = "Sync cannot be enabled yet",
    description = "These question bank rows are missing on the other universities. Sync them from the question bank first, then enable sync here.",
    // A tutorial is not on a matrix, so its refusal has no matrix to name.
    showMatrix = true,
}) => (
    <BlockingRowsDialog
        rows={unsyncedCategories}
        columns={showMatrix ? UNSYNCED_COLUMNS : UNSYNCED_COLUMNS.filter(([label]) => label !== "Matrix")}
        onClose={onClose}
        title={title}
        description={description}
    />
);

/**
 * An import into a library matrix refused because some of the chosen
 * assignments still use question bank categories or subcategories that have
 * been deleted since. Nothing was imported; the way through is to take those
 * off the assignments in the old semester and import again.
 */
export const DeletedCategoriesDialog = ({ deletedCategories, onClose }) => (
    <BlockingRowsDialog
        rows={deletedCategories}
        columns={DELETED_COLUMNS}
        onClose={onClose}
        title="These assignments use deleted categories"
        description={"The assignments below use question bank categories or subcategories that have been deleted, "
            + "so nothing was imported. Remove them from these assignments in the old semester, then import again."}
    />
);

/**
 * The reverse refusal, shown on the question bank's own sync toggles: sync
 * cannot be switched off for a subject, topic or category while a library
 * matrix that is syncing needs its categories at the other universities.
 *
 * One dialog for all three levels because one gate answers all three - a topic
 * or subject switch takes every category under it down with it, so the
 * refusal is the same and only the noun changes.
 *
 * It names the matrices rather than only refusing, because the way through is
 * to switch sync off on those matrices first - and nothing on the question
 * bank page says which they are.
 */
const SCOPE_DEPENDENCY = {
    category: "this category",
    topic: "categories in this topic",
    subject: "categories in this subject",
};

export const QBSyncInUseByLibraryDialog = ({
    libraryMatrices,
    onClose,
    scope = "category",
}) => (
    <BlockingRowsDialog
        rows={libraryMatrices}
        columns={LIBRARY_USAGE_COLUMNS}
        onClose={onClose}
        title={`Sync cannot be turned off for this ${scope}`}
        description={`These library matrices are syncing to the other universities and depend on `
            + `${SCOPE_DEPENDENCY[scope] || SCOPE_DEPENDENCY.category}. `
            + `Turning sync off here would leave them pushing questions the other universities do not have. `
            + `Turn sync off for these matrices first, then turn it off here.`}
    />
);
